import { HttpApiService } from "@/shared/services/httpApi.service";

import { ORGANIZATIONS_ADMIN_ENDPOINTS } from "../constants/organizationsAdmin.constants";
import type {
  AdminOrganization,
  AdminOrganizationsPage,
  AdminOrganizationsQuery,
  GlobalFeature,
  GlobalFeatureMode,
  GlobalFeatures,
  GlobalFeaturesResponse,
  OrganizationOverview,
  UpdateGlobalFeaturesPayload,
  UpdateOrganizationFeaturesPayload,
} from "../types/organizationsAdmin.types";
import { GLOBAL_FEATURE_MODES } from "../types/organizationsAdmin.types";

/**
 * `modes` when the backend sends it; before it did, the boolean (`true` = on for all, anything else = each
 * organization decides). A backend that predates a switch omits it, which reads as each organization
 * deciding.
 */
export function readGlobalFeatures(data: GlobalFeaturesResponse | null | undefined): GlobalFeatures {
  const read = (feature: GlobalFeature): GlobalFeatureMode => {
    const mode = data?.modes?.[feature];
    if (isGlobalFeatureMode(mode)) return mode;
    return data?.[feature] === true ? "on_for_all" : "per_organization";
  };
  return {
    payout_batches_enabled: read("payout_batches_enabled"),
    breb_receiving_key_enabled: read("breb_receiving_key_enabled"),
    otc_enabled: read("otc_enabled"),
    card_deposits_enabled: read("card_deposits_enabled"),
    card_cop_top_ups_enabled: read("card_cop_top_ups_enabled"),
    breb_qr_payments_enabled: read("breb_qr_payments_enabled"),
  };
}

function isGlobalFeatureMode(value: unknown): value is GlobalFeatureMode {
  return GLOBAL_FEATURE_MODES.includes(value as GlobalFeatureMode);
}

export function organizationsQueryString(query: AdminOrganizationsQuery): string {
  const params = new URLSearchParams();
  params.set("skip", String(query.skip));
  params.set("limit", String(query.limit));
  if (query.search) params.set("search", query.search);
  if (query.status) params.set("status", query.status);
  if (query.payoutBatchesEnabled !== undefined) {
    params.set("payout_batches_enabled", String(query.payoutBatchesEnabled));
  }
  return params.toString();
}

export const OrganizationsAdminService = {
  list(query: AdminOrganizationsQuery) {
    return HttpApiService.get<AdminOrganizationsPage>(
      `${ORGANIZATIONS_ADMIN_ENDPOINTS.LIST}?${organizationsQueryString(query)}`,
    );
  },

  async getOverview(id: string): Promise<OrganizationOverview> {
    const { data } = await HttpApiService.get<OrganizationOverview>(
      ORGANIZATIONS_ADMIN_ENDPOINTS.OVERVIEW(id),
    );
    return data;
  },

  updateFeatures(id: string, payload: UpdateOrganizationFeaturesPayload) {
    return HttpApiService.patch<AdminOrganization>(ORGANIZATIONS_ADMIN_ENDPOINTS.FEATURES(id), payload);
  },

  getGlobalFeatures() {
    return HttpApiService.get<GlobalFeaturesResponse>(ORGANIZATIONS_ADMIN_ENDPOINTS.GLOBAL_FEATURES);
  },

  updateGlobalFeatures(payload: UpdateGlobalFeaturesPayload) {
    return HttpApiService.patch<GlobalFeaturesResponse>(
      ORGANIZATIONS_ADMIN_ENDPOINTS.GLOBAL_FEATURES,
      payload,
    );
  },

  async getCardCopTopUpsAccess(): Promise<boolean> {
    const { data } = await HttpApiService.get<{ allowed: boolean }>(
      ORGANIZATIONS_ADMIN_ENDPOINTS.CARD_COP_TOP_UPS_ACCESS,
    );
    return data.allowed;
  },
};
